import { lazy, Suspense, useEffect, useState } from 'react'
import { File, ExternalLink, Eye, Loader2 } from 'lucide-react'
import { Card, CardContent } from '../ui/card'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog'
import { useTheme } from '../admin/use-theme'
import { cn } from '../../lib/utils'

/**
 * `AttachmentLink` (table-cell "View file" links, HATCH-1291) pulled
 * `AttachmentsList` into apps that only ever preview an occasional image,
 * never a pdf — a top-level `@embedpdf/react-pdf-viewer` import drags its
 * ~4.6 MB pdfium wasm into every one of those apps' main bundle regardless.
 * Deferred to a dynamic import so it's only fetched the moment a pdf
 * preview actually opens.
 */
const PDFViewer = lazy(() => import('@embedpdf/react-pdf-viewer').then((module) => ({ default: module.PDFViewer })))

export interface Attachment {
  file_id: string
  name?: string | null
  url?: string | null
}

const IMAGE_EXTENSIONS = new Set(['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'])

/**
 * The generic private-file endpoint streams any file type through one route
 * and doesn't reliably set a matching `Content-Type` (HATCH-1157 review: pdf
 * "download still happens" even after fetching it as a blob) — a `Blob`'s
 * `type` normally comes straight from the response's `Content-Type` header,
 * and a browser's `<iframe src="blob:...">` only invokes its built-in PDF
 * viewer when that type is actually `application/pdf`; anything else (e.g. a
 * generic `application/octet-stream`) falls back to downloading it, same as
 * the `Content-Disposition: attachment` problem this was meant to fix. So
 * the MIME type is forced from the known extension instead of trusted from
 * the server.
 */
const MIME_TYPES: Record<string, string> = {
  pdf: 'application/pdf',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  gif: 'image/gif',
  webp: 'image/webp',
  svg: 'image/svg+xml',
  bmp: 'image/bmp',
}

/** `name` is the human filename (has the real extension); `url` is a signed download link that may not end in one, so it's only a fallback. */
function getExtension({ name, url }: Attachment): string {
  const fileName = (name ?? url ?? '').split(/[?#]/)[0]
  const dotIndex = fileName.lastIndexOf('.')
  return dotIndex === -1 ? '' : fileName.slice(dotIndex + 1).toLowerCase()
}

export function getAttachmentPreviewKind(attachment: Attachment): 'image' | 'pdf' | null {
  const extension = getExtension(attachment)
  if (extension === 'pdf') return 'pdf'
  if (IMAGE_EXTENSIONS.has(extension)) return 'image'
  return null
}

type PreviewState = { status: 'idle' } | { status: 'loading' } | { status: 'ready'; objectUrl: string } | { status: 'error' }

/**
 * The private-file download endpoint behind an attachment's `url` responds
 * with `Content-Disposition: attachment`, which makes a browser
 * force-download the response for any direct reference to that URL — an
 * `<img>`/`<iframe> src` included, even inside a dialog (HATCH-1157 review:
 * a pdf attachment "downloaded... instead of show up in preview"). Fetching
 * it ourselves and handing the bytes to `<img>`/`<iframe>` as a local
 * `blob:` object URL sidesteps that header entirely — it only ever governs
 * how the browser handles a fetch of the *original* URL, never a
 * locally-created object URL with no headers of its own.
 */
function usePreviewObjectUrl(attachment: Attachment | null): PreviewState {
  const url = attachment?.url
  const extension = attachment ? getExtension(attachment) : ''
  const [state, setState] = useState<PreviewState>({ status: 'idle' })

  useEffect(() => {
    if (!url) {
      setState({ status: 'idle' })
      return
    }

    let cancelled = false
    let objectUrl: string | undefined
    setState({ status: 'loading' })

    fetch(url)
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`)
        return response.blob()
      })
      .then((blob) => {
        if (cancelled) return
        const mimeType = MIME_TYPES[extension]
        objectUrl = URL.createObjectURL(mimeType ? new Blob([blob], { type: mimeType }) : blob)
        setState({ status: 'ready', objectUrl })
      })
      .catch(() => {
        if (!cancelled) setState({ status: 'error' })
      })

    return () => {
      cancelled = true
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [url, extension])

  return state
}

function AttachmentPreviewDialog({ attachment, onOpenChange }: { attachment: Attachment | null; onOpenChange: (open: boolean) => void }) {
  const kind = attachment ? getAttachmentPreviewKind(attachment) : null
  const preview = usePreviewObjectUrl(attachment)
  const { theme } = useTheme()

  return (
    <Dialog open={attachment != null} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-[100dvh] w-screen max-w-none flex-col gap-2 rounded-none border-0 p-4 sm:h-[90dvh] sm:w-[90vw] sm:max-w-4xl sm:rounded-lg sm:border lg:max-w-6xl 2xl:max-w-7xl">
        <DialogHeader>
          <DialogTitle className="truncate pr-8">{attachment?.name}</DialogTitle>
        </DialogHeader>

        {preview.status === 'idle' || preview.status === 'loading' ? (
          <div className="flex flex-1 items-center justify-center">
            <Loader2 className="text-muted-foreground size-8 animate-spin" aria-hidden="true" />
          </div>
        ) : null}

        {preview.status === 'error' ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-2 text-sm">
            <span className="text-muted-foreground">Couldn't load the preview.</span>
            {attachment?.url ? (
              <a href={attachment.url} target="_blank" rel="noopener noreferrer" className="text-primary underline">
                Open in a new tab
              </a>
            ) : null}
          </div>
        ) : null}

        {preview.status === 'ready' && kind === 'image' ? (
          <img src={preview.objectUrl} alt={attachment?.name ?? ''} className="min-h-0 flex-1 object-contain" />
        ) : null}

        {preview.status === 'ready' && kind === 'pdf' ? (
          // A native <iframe>'s built-in PDF renderer only shows page one on
          // touch devices, with no scroll/pagination (HATCH-1177, a WebKit/
          // Chrome platform limitation) — EmbedPdf renders every page itself
          // instead of delegating to the browser, so it works the same on an
          // iPad as anywhere else.
          //
          // `PDFViewer` mounts as a custom element with its own shadow DOM,
          // and the actual scrollable (overflow: auto) content lives inside
          // that shadow root. Radix's Dialog scroll lock (react-remove-scroll)
          // decides whether to let a wheel/touch gesture through by walking
          // up from `event.target` to find a scrollable ancestor — but a
          // listener outside the shadow tree only ever sees the shadow host
          // as `event.target`, never the real scrollable element inside it,
          // so the walk always concludes "nothing scrollable here" and the
          // lock swallows every gesture (HATCH-1230: trackpad scroll did
          // nothing, no scrollbar ever appeared on iPad). Stopping
          // propagation here keeps the event from ever reaching the lock's
          // document-level listener, so the browser's native scroll inside
          // the shadow root proceeds unobstructed.
          <div
            className="min-h-0 flex-1"
            onWheel={(event) => event.stopPropagation()}
            onTouchMove={(event) => event.stopPropagation()}
          >
            {/* `PDFViewer` only ever sizes its own mount element from the
                `className`/`style` handed to it directly (it does nothing
                with an ambient flex/percentage context from a wrapping
                element) — a sizeless wrapper around it leaves EmbedPdf
                measuring a 0-height target, rendering only its own
                fixed-position toolbar with nothing below it (review
                feedback: "I see only toolbar"). */}
            <Suspense
              fallback={
                <div className="flex h-full items-center justify-center">
                  <Loader2 className="text-muted-foreground size-8 animate-spin" aria-hidden="true" />
                </div>
              }
            >
              <PDFViewer
                className="h-full"
                config={{
                  src: preview.objectUrl,
                  theme: { preference: theme },
                  disabledCategories: ['annotation', 'insert', 'form', 'redaction'],
                }}
              />
            </Suspense>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  )
}

/**
 * A single inline "View file" text link (table cells, compact rows) — same
 * preview behavior as `AttachmentsList`'s cards (image/pdf opens the
 * fullscreen in-page dialog via the private-file blob fetch, anything else
 * opens/downloads in a new tab), without the full-width `Card` chrome a
 * dense table row has no room for.
 */
export function AttachmentLink({ attachment }: { attachment: Attachment }) {
  const [open, setOpen] = useState(false)
  const kind = getAttachmentPreviewKind(attachment)

  if (kind) {
    return (
      <>
        <button type="button" onClick={() => setOpen(true)} className="text-sm underline">
          View file
        </button>
        <AttachmentPreviewDialog attachment={open ? attachment : null} onOpenChange={setOpen} />
      </>
    )
  }

  return (
    <a href={attachment.url ?? undefined} target="_blank" rel="noreferrer" className="text-sm underline">
      View file
    </a>
  )
}

/**
 * One full-width card per attachment — a generic file icon, the attachment
 * name, and either an `Eye` affordance (image/pdf: the whole card opens a
 * fullscreen in-page preview) or an `ExternalLink` one (anything else: opens
 * or downloads the file in a new tab — the file type is unknown so the
 * browser is the only thing that knows how to handle it). Renders nothing
 * when `attachments` is empty, so callers can render it unconditionally.
 */
export function AttachmentsList({ attachments, className }: { attachments: Attachment[] | null | undefined; className?: string }) {
  const [previewAttachment, setPreviewAttachment] = useState<Attachment | null>(null)
  if (!attachments || attachments.length === 0) return null

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      {attachments.map((attachment) => {
        const kind = getAttachmentPreviewKind(attachment)
        const card = (
          <Card className="transition-colors hover:bg-accent/50">
            <CardContent className="flex items-center gap-3">
              <File className="text-muted-foreground size-5 shrink-0" aria-hidden="true" />
              <span className="flex-1 truncate">{attachment.name}</span>
              {kind ? (
                <Eye className="text-muted-foreground size-4 shrink-0" aria-hidden="true" />
              ) : (
                <ExternalLink className="text-muted-foreground size-4 shrink-0" aria-hidden="true" />
              )}
            </CardContent>
          </Card>
        )

        if (kind) {
          return (
            <button key={attachment.file_id} type="button" className="block w-full text-left" onClick={() => setPreviewAttachment(attachment)}>
              {card}
            </button>
          )
        }

        return (
          <a key={attachment.file_id} href={attachment.url ?? undefined} target="_blank" rel="noopener noreferrer" className="block">
            {card}
          </a>
        )
      })}

      <AttachmentPreviewDialog attachment={previewAttachment} onOpenChange={(open) => !open && setPreviewAttachment(null)} />
    </div>
  )
}
